import { useMutation } from '@apollo/client';
import { countBy, groupBy, isNil, noop, some, sum, uniqueId } from 'lodash';
import { createContext, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Mutations } from '@shared/api';
import { useLocalWorkflowEntityChanges } from '@shared/data/cards/hooks/LocalWorkflowEntityChanges';
import { FieldRecord } from '@shared/scenes/entity/types';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { SUBTABLE_ROW_LIMIT } from './subtableRowLimit';
import { SubtableRows } from './useSubtableRows';

/** A row added on the Create form, held here until its parent card exists. */
export interface SubtableDraftRow {
  /** A temporary id, standing in for the entity id the row gets once it's created. */
  id: string;
  /** The parent's subtable field the row belongs to. */
  componentId: string;
  workflowTemplate: API.WorkflowTemplateFragment;
  /** The subtable's Create view template the row was filled in with; it validates the create. */
  viewTemplateId?: string | null;
  fields: FieldRecord;
  createdAt: string;
  /**
   * The row's entity, once it's been created under the card. A saved draft stays in the drafts,
   * read-only, so a retry after a partial failure still shows it and counts it toward the row limit.
   */
  savedEntityId?: string;
}

/** The Create form's draft rows, across all of its subtable fields. */
export interface SubtableDrafts {
  rows: SubtableDraftRow[];
  addRow: (row: Omit<SubtableDraftRow, 'id' | 'createdAt'>) => void;
  updateRow: (id: string, fields: FieldRecord) => void;
  deleteRow: (id: string) => void;
  /** Marks a row form (by its own key) as open with unsaved changes, or not. */
  setRowFormDirty: (key: string, isDirty: boolean) => void;
}

/**
 * The Create form's draft rows. Unset everywhere else, including a new row's own form: a
 * subtable inside a row that doesn't exist yet stays hidden.
 */
export const SubtableDraftsContext = createContext<SubtableDrafts | undefined>(undefined);

export interface SubtableDraftsState extends SubtableDrafts {
  /** Any draft rows, which count as unsaved changes. */
  hasRows: boolean;
  /** A row form is open with changes that haven't been added to the drafts. */
  hasDirtyRowForm: boolean;
  /** Some subtable holds more drafts than the backend takes per parent. */
  isOverRowLimit: boolean;
  /**
   * Creates the unsaved drafts under the parent, one batch per subtable field in the order they were
   * added, and marks each batch that saves as saved. Resolves to how many rows didn't, which stay
   * unsaved for a retry.
   */
  saveRows: (parentEntityId: string) => Promise<number>;
  reset: () => void;
}

/** Holds a Create form's draft rows; the form provides it through SubtableDraftsContext. */
export function useSubtableDraftsState(): SubtableDraftsState {
  const [rows, setRows] = useState<SubtableDraftRow[]>([]);
  const [dirtyRowForms, setDirtyRowForms] = useState<Record<string, boolean>>({});
  const [createEntities] = useMutation<
    API.CreateWorkflowEntitiesMutation,
    API.CreateWorkflowEntitiesMutationVariables
  >(Mutations.entity.createWorkflowEntities);
  const { publish } = useLocalWorkflowEntityChanges();

  const addRow = useCallback((row: Omit<SubtableDraftRow, 'id' | 'createdAt'>) => {
    const draft = { ...row, id: uniqueId('subtable-draft-'), createdAt: new Date().toISOString() };
    setRows(previousRows => [...previousRows, draft]);
  }, []);

  const updateRow = useCallback((id: string, fields: FieldRecord) => {
    setRows(previousRows => previousRows.map(row => (row.id === id ? { ...row, fields } : row)));
  }, []);

  const deleteRow = useCallback((id: string) => {
    setRows(previousRows => previousRows.filter(row => row.id !== id));
  }, []);

  const setRowFormDirty = useCallback((key: string, isDirty: boolean) => {
    setDirtyRowForms(previous => {
      if (!!previous[key] === isDirty) {
        return previous;
      }
      return { ...previous, [key]: isDirty };
    });
  }, []);

  const saveRows = useCallback(
    async (parentEntityId: string) => {
      // Assumes a batch is all or nothing, and gives its rows ascending createdAt in the order sent.
      const unsavedRows = rows.filter(row => isNil(row.savedEntityId));
      const batches = Object.values(groupBy(unsavedRows, row => row.componentId));
      const failedCounts = await Promise.all(
        batches.map(async batch => {
          const { workflowTemplate, viewTemplateId } = batch[0];
          try {
            const result = await createEntities({
              variables: {
                input: {
                  workflowTemplateId: workflowTemplate.id,
                  viewTemplateId,
                  entities: batch.map(row => ({
                    workflowTemplateId: workflowTemplate.id,
                    viewTemplateId,
                    fields: toFieldJson(row.fields, workflowTemplate),
                    parentEntityId,
                  })),
                },
              },
            });
            const entities = result.data?.createWorkflowEntities?.entities?.filter(notNil) ?? [];
            if (entities.length !== batch.length) {
              throw new Error('Not every subtable row was created');
            }
            publish({ type: 'WORKFLOW_ENTITY_CREATED', entities, previousEntities: [] });
            const savedEntityIds = new Map(batch.map((row, index) => [row.id, entities[index].id]));
            setRows(previousRows =>
              previousRows.map(row => {
                const savedEntityId = savedEntityIds.get(row.id);
                return isNil(savedEntityId) ? row : { ...row, savedEntityId };
              }),
            );
            return 0;
          } catch (error) {
            console.error(error);
            return batch.length;
          }
        }),
      );
      return sum(failedCounts);
    },
    [rows, createEntities, publish],
  );

  const reset = useCallback(() => {
    setRows([]);
    setDirtyRowForms({});
  }, []);

  const isOverRowLimit = useMemo(
    () =>
      some(
        countBy(rows, row => row.componentId),
        count => count > SUBTABLE_ROW_LIMIT,
      ),
    [rows],
  );

  const hasDirtyRowForm = some(dirtyRowForms);
  return useMemo(
    () => ({
      rows,
      addRow,
      updateRow,
      deleteRow,
      setRowFormDirty,
      hasRows: rows.length > 0,
      hasDirtyRowForm,
      isOverRowLimit,
      saveRows,
      reset,
    }),
    [
      rows,
      addRow,
      updateRow,
      deleteRow,
      setRowFormDirty,
      hasDirtyRowForm,
      isOverRowLimit,
      saveRows,
      reset,
    ],
  );
}

/** One subtable field's draft rows, from the Create form's. */
export interface SubtableFieldDrafts {
  rows: SubtableDraftRow[];
  add: (fields: FieldRecord, viewTemplateId?: string | null) => void;
  update: (id: string, fields: FieldRecord) => void;
  remove: (id: string) => void;
  setRowFormDirty: (key: string, isDirty: boolean) => void;
}

/**
 * A subtable field's draft rows on the Create form, in the shape of its loaded rows, so the table,
 * list and row menu render them the same way. Oldest first, like loaded rows.
 */
export function useDraftSubtableRows(
  component: API.SubtableComponent,
  drafts: SubtableDrafts,
): { subtableRows: SubtableRows; fieldDrafts: SubtableFieldDrafts } {
  const { workflowTemplate, loading, error } = useWorkflowTemplate(component.workflowTemplateId);
  const fieldDrafts = useSubtableFieldDrafts(drafts, component.id, workflowTemplate);
  const rows = useMemo(() => fieldDrafts.rows.map(toDraftRowEntity), [fieldDrafts.rows]);
  const subtableRows = useMemo(
    () => ({
      rows,
      refs: undefined,
      totalResults: rows.length,
      hasMore: false,
      loadMore: () => Promise.resolve(),
      refetch: noop,
      loading,
      error,
      workflowTemplate,
    }),
    [rows, loading, error, workflowTemplate],
  );
  return { subtableRows, fieldDrafts };
}

function useSubtableFieldDrafts(
  drafts: SubtableDrafts,
  componentId: string,
  workflowTemplate: API.WorkflowTemplateFragment | undefined,
): SubtableFieldDrafts {
  const { rows, addRow, updateRow, deleteRow, setRowFormDirty } = drafts;
  const fieldRows = useMemo(
    () => rows.filter(row => row.componentId === componentId),
    [rows, componentId],
  );
  return useMemo(
    () => ({
      rows: fieldRows,
      add: (fields, viewTemplateId) => {
        if (!isNil(workflowTemplate)) {
          addRow({ componentId, workflowTemplate, viewTemplateId, fields });
        }
      },
      update: updateRow,
      remove: deleteRow,
      setRowFormDirty,
    }),
    [fieldRows, workflowTemplate, componentId, addRow, updateRow, deleteRow, setRowFormDirty],
  );
}

/** Tells the drafts while a row form has unsaved changes, so Create can ask before dropping them. */
export function useReportDirtyRowForm(drafts: SubtableFieldDrafts | undefined, isDirty: boolean) {
  const key = useId();
  const setRowFormDirty = drafts?.setRowFormDirty;
  useEffect(() => {
    setRowFormDirty?.(key, isDirty);
    return () => setRowFormDirty?.(key, false);
  }, [setRowFormDirty, key, isDirty]);
}

// The table and list read a row's cells from its entity's raw field data. A draft's cells come
// straight from its field record instead: it never went through the server, which is what turns
// field input into entity data.
const draftRowFields = new WeakMap<API.WorkflowEntityFragment, FieldRecord>();

/** A draft row as the stand-in entity the table, list and row menu render. */
export function toDraftRowEntity(row: SubtableDraftRow): API.WorkflowEntityFragment {
  const entity: API.WorkflowEntityFragment = {
    id: row.id,
    workflowTemplateId: row.workflowTemplate.id,
    createdAt: row.createdAt,
    updatedAt: row.createdAt,
    fields: {},
    threadId: '',
    channelId: 0,
  };
  draftRowFields.set(entity, row.fields);
  return entity;
}

/** Whether a row is a draft, whose cells can't act on it (an input button, a timer) until it's created. */
export function isDraftRow(entity: API.WorkflowEntityFragment): boolean {
  return draftRowFields.has(entity);
}

/** A draft row's field records; undefined for a saved row. */
export function getDraftRowFields(entity: API.WorkflowEntityFragment): FieldRecord | undefined {
  return draftRowFields.get(entity);
}
